import * as React from "react"
import { cva, type VariantProps } from "class-variance-authority"

import { cn } from "@/lib/utils"

const alertVariants = cva(
  "relative w-full rounded-lg border p-4 [&>svg~*]:pl-7 [&>svg+div]:translate-y-[-3px] [&>svg]:absolute [&>svg]:left-4 [&>svg]:top-4 [&>svg]:text-foreground",
  {
    variants: {
      variant: {
        default: "bg-background text-foreground",
        destructive:
          "border-destructive/30 bg-destructive-muted text-destructive-muted-foreground [&>svg]:text-destructive-muted-foreground",
        // The feedback -muted pairs, as on Badge. `tip` is success: accent is
        // only a hover or selected surface (colour spec rule 3).
        highlight: "border-highlight bg-highlight-muted text-highlight-muted-foreground [&>svg]:text-highlight-muted-foreground",
        info: "border-info/30 bg-info-muted text-info-muted-foreground [&>svg]:text-info-muted-foreground",
        warning:
          "border-warning/30 bg-warning-muted text-warning-muted-foreground [&>svg]:text-warning-muted-foreground",
        tip: "border-success/30 bg-success-muted text-success-muted-foreground [&>svg]:text-success-muted-foreground",
        // A quiet note that is not feedback: a hint above a field, a status strip.
        muted: "border-transparent bg-muted text-muted-foreground [&>svg]:text-muted-foreground",
      },
      // `sm`, `banner` and `strip` lay the icon out inline (flex) instead of absolutely.
      size: {
        step: "flex items-start gap-3 px-4 py-3 text-xs",
        default: "",
        // The default box with the icon in the row, centred on the first text line however
        // many lines follow; the default's absolute icon sits above a single line's centre (BRE-9100).
        banner: "flex items-start gap-3 [&>svg]:static [&>svg]:mt-0.5 [&>svg]:shrink-0 [&>svg~*]:pl-0 [&>svg+div]:min-w-0 [&>svg+div]:flex-1 [&>svg+div]:translate-y-0",
        // A rich-text callout (BRE-9890): a 12px inset, an 8px gap, and the icon centred on
        // the first line of a py-1 paragraph at 1.3rem.
        callout: "flex items-start gap-2 p-3 [&>svg]:static [&>svg]:mt-1.5 [&>svg]:shrink-0 [&>svg~*]:pl-0 [&>svg+div]:min-w-0 [&>svg+div]:flex-1 [&>svg+div]:translate-y-0",
        sm: "flex items-start gap-2 rounded-md px-3 py-2 text-xs [&>svg]:static [&>svg]:mt-px [&>svg]:size-3.5 [&>svg]:shrink-0 [&>svg~*]:pl-0 [&>svg+div]:translate-y-0",
        // Full-width strip under a page header: bottom border only, no radius.
        strip: "flex items-center gap-2 rounded-none border-0 border-b px-6 py-2 text-sm mobile:px-3 [&>svg]:static [&>svg]:size-4 [&>svg]:shrink-0 [&>svg~*]:pl-0 [&>svg+div]:translate-y-0",
      },
    },
    compoundVariants: [
      { variant: "muted", size: "strip", class: "border-border/40 bg-muted/30" },
    ],
    defaultVariants: {
      variant: "default",
      size: "default",
    },
  }
)

// `role` defaults to "alert", which screen readers announce on render; a static
// note on a page (a docs callout) passes role="note" instead.
const Alert = React.forwardRef<
  HTMLDivElement,
  React.HTMLAttributes<HTMLDivElement> & VariantProps<typeof alertVariants>
>(({ className, variant, size, role = "alert", ...props }, ref) => (
  <div
    ref={ref}
    role={role}
    className={cn(alertVariants({ variant, size }), className)}
    {...props}
  />
))
Alert.displayName = "Alert"

const AlertDescription = React.forwardRef<
  HTMLParagraphElement,
  React.HTMLAttributes<HTMLParagraphElement>
>(({ className, ...props }, ref) => (
  <div
    ref={ref}
    className={cn("text-sm [&_p]:leading-relaxed", className)}
    {...props}
  />
))
AlertDescription.displayName = "AlertDescription"

export { Alert, alertVariants, AlertDescription }
